import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiRequests } from "../services/ApiRequest";
import { toast } from "react-toastify";

const PAGE_SIZE = 100;

// Fetch one page of transactions (server-side pagination)
export const getTransactionsPage = createAsyncThunk(
  "transactions/getTransactionsPage",
  async (page = 0, { rejectWithValue }) => {
    try {
      const response = await ApiRequests.getTransactions({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      });
      if (response.status === 200) {
        return { ...response.data.RESPONSE, page };
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.ERROR?.message ?? "Failed to load transactions");
    }
  }
);

// Fetch ALL transactions — only used for CSV download
export const downloadAllTransactions = createAsyncThunk(
  "transactions/downloadAllTransactions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiRequests.getTransactions();
      if (response.status === 200) {
        return response.data.RESPONSE.transactions;
      }
    } catch (error) {
      toast.error("Failed to prepare download. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
        theme: "light",
      });
      return rejectWithValue(error?.response?.data?.ERROR?.message ?? "Download failed");
    }
  }
);

// add article price
export const addArticlePrice = createAsyncThunk(
  "transactions/addArticlePrice",
  async ({ publisherId, data, onReset }, { rejectWithValue }) => {
    try {
      const response = await ApiRequests.addArticlePrice(publisherId, data);
      if (response.status === 200) {
        onReset();
        toast.success("Article Price Updated!", {
          position: "top-right",
          autoClose: 3000,
          closeOnClick: true,
          pauseOnHover: true,
          theme: "light",
        });
        return response?.data?.RESPONSE;
      }
    } catch (error) {
      toast.error(error?.response?.data?.ERROR?.message, {
        position: "top-right",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
        theme: "light",
      });
    }
  }
);

const initialState = {
  transactions: [],
  total: 0,
  currentPage: 0,
  loading: false,
  downloadLoading: false,
  downloadData: null,
};

const transactionSlice = createSlice({
  name: "transaction",
  initialState,
  reducers: {
    clearDownloadData(state) {
      state.downloadData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransactionsPage.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTransactionsPage.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
      })
      .addCase(getTransactionsPage.rejected, (state) => {
        state.loading = false;
      })

      .addCase(downloadAllTransactions.pending, (state) => {
        state.downloadLoading = true;
        state.downloadData = null;
      })
      .addCase(downloadAllTransactions.fulfilled, (state, action) => {
        state.downloadLoading = false;
        state.downloadData = action.payload;
      })
      .addCase(downloadAllTransactions.rejected, (state) => {
        state.downloadLoading = false;
      })

      .addCase(addArticlePrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(addArticlePrice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addArticlePrice.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearDownloadData } = transactionSlice.actions;
export default transactionSlice.reducer;
